import React, { useMemo, useState } from 'react';
import IconChevron from '../Icons/IconChevron/IconChevron';
import IconCrossRound from '../Icons/IconCrossRound/IconCrossRound';

export type Option = {
  id: string;
  value: string | number;
  [key: string]: any | any[];
}

export type SelectedValue = {
  id: string;
  value: string | number;
}

type ComponentProps = {
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  options: Option[];
  title: string;
  selectedOption: Option | null;
  name: string;
  disabled?: boolean;
  placeholder?: string
  errorText?: string;
  showError?: boolean | string;
  customBlockClassName?: string;
  customLabelClassName?: string;
};

const SelectOption: React.FC<ComponentProps> = ({
  onChange,
  selectedOption,
  title,
  name,
  options,
  disabled = false,
  placeholder = 'Select your option',
  errorText,
  showError,
  customBlockClassName,
  customLabelClassName,
}) => {
  const DEFAULT_OPTION: Option = {
    id: 'DEFAULT_OPTION',
    value: ''
  };

  const currentOptions: Option[] = useMemo(() => {
    if (!selectedOption) {
      return [DEFAULT_OPTION, ...options];
    }

    return options;
  }, [selectedOption, options]);
  const currentSelectedOption = useMemo(() => selectedOption?.value ?? DEFAULT_OPTION.value, [selectedOption]);

  const [isOptionSelected, setIsOptionSelected] = useState<boolean>(false);

  const changeTextColor = () => {
    setIsOptionSelected(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = event.target.selectedOptions[0];
    const valueId = selectedOption.dataset.id;

    const selectedValue: SelectedValue = { id: valueId, value: event.target.value, };

    const customEvent = {
      ...event,
      target: {
        ...event.target,
        name: event.target.name,
        value: selectedValue,
      }
    } as unknown as React.ChangeEvent<HTMLSelectElement>;
    onChange(customEvent);
    changeTextColor();
  }

  return (
    <div className={`c-select-option select-none ${disabled ? 'pointer-events-none' : ''} ${customBlockClassName ?? ''}`}>
      <label className={customLabelClassName ?? 'flex justify-between pb-1.5 2xl:pb-2.5 font-medium text-sm 2xl:text-base text-black dark:text-white'}>
        {title}
      </label>
      <div className="relative z-20 bg-transparent dark:bg-form-input">
        <select
          name={name}
          value={currentSelectedOption}
          onChange={handleChange}
          className={`relative z-20 w-full cursor-pointer appearance-none rounded-lg border ${disabled ? 'border-black bg-zinc-400' : 'border-stroke bg-transparent'} px-6 py-4 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary h-15 ${isOptionSelected ? 'text-black dark:text-white' : ''
          } ${selectedOption ? '' : 'text-[#9ca3af]'}`}
        >
          {currentOptions.map((option) => {
            if (option.value === DEFAULT_OPTION.value) {
              return (
                <option key={DEFAULT_OPTION.id} value={DEFAULT_OPTION.value} disabled >
                  {placeholder}
                </option>
              )
            }
            return (
              <option key={option.id} data-id={option.id} value={option.value}>
                {option.value}
              </option>
            )
          })}
        </select>
        <span className="absolute right-4 top-1/2 -translate-y-1/2">
          {disabled ? (<IconCrossRound />) : (<IconChevron width="30px" height="28px" />)}
        </span>
      </div>
      <div className="flex min-h-6 items-center justify-start">
        {showError && <p className="text-sm text-red">{errorText}</p>}
      </div>
    </div>
  );
};

export default SelectOption;
